import { addWeeks, differenceInDays, differenceInMonths, addMonths } from 'date-fns'

const BodyMassIndexCalculator = {
	calc: (weightInKilograms, heightInMeters) => {
		if (heightInMeters <= 0) {
			return null
		}

		return weightInKilograms / (heightInMeters * heightInMeters)
	},
}

export const WeightLossCalculator = {
	/**
	 * Predicts weight loss for different difficulties
	 * @param weight in kg
	 * @param weightGoal in kg
	 * @param height in cm
	 * @returns {{difficulty: string, numberOfWeeks: number, firstSpeedInKg, targetDate: Date}[]}
	 */
	predictWeightLoss: ({ weight, weightGoal, height }) => {
		const heightInMeters = height / 100
		const predictParams = [
			{
				// 1.85 lbs/week
				difficulty: 'easy',
				defaultSpeed: 0.84,
				bmiToSpeed: [{ bmiRange: { lowerBound: 0, upperBound: Infinity }, speed: 0.84 }],
			},
			{
				// 2.8 lbs/week
				difficulty: 'normal',
				defaultSpeed: 1.27,
				bmiToSpeed: [{ bmiRange: { lowerBound: 0, upperBound: Infinity }, speed: 1.27 }],
			},
			{
				// 3.7 lbs/week
				difficulty: 'hard',
				defaultSpeed: 1.68,
				bmiToSpeed: [{ bmiRange: { lowerBound: 0, upperBound: Infinity }, speed: 1.68 }],
			},
		]

		return predictParams.map((param) =>
			WeightLossCalculator.predictWeightLossForSpeed({
				speed: param,
				weight,
				weightGoal,
				height: heightInMeters,
			}),
		)
	},
	predictWeightLossForSpeed: ({ speed, weight, weightGoal, height }) => {
		let currentWeight = weight
		let numberOfWeeks = 0
		let firstSpeed = null
		while (currentWeight > weightGoal) {
			const bmi = BodyMassIndexCalculator.calc(currentWeight, height) ?? 0
			const weekSpeed =
				speed.bmiToSpeed.find((item) => bmi >= item.bmiRange.lowerBound && bmi < item.bmiRange.upperBound)?.speed ??
				speed.defaultSpeed
			currentWeight -= weekSpeed
			numberOfWeeks += 1
			if (firstSpeed === null) {
				firstSpeed = weekSpeed
			}
		}
		const today = new Date()
		let targetDate = addWeeks(today, numberOfWeeks)
		// DIRTY HACK minimum 3 months of fasting instead of real number
		// consider removing
		if (Math.abs(differenceInMonths(today, targetDate)) < 3) {
			targetDate = addMonths(today, 3)
			// The week count is quoted in copy next to this date (`predictionWeeksNormal`
			// on goal-pace vs `predictionDate_2` on teaser are the two fields of THIS
			// object), so it has to follow the floor as well — otherwise a goal reachable
			// in four weeks, or in zero when the user already weighs their target, reads
			// as "in 4 weeks" beside a date three months out. Rounded off the day count
			// because date-fns 2's `differenceInWeeks` truncates and would under-report.
			numberOfWeeks = Math.round(differenceInDays(targetDate, today) / 7)
		}
		return {
			difficulty: speed.difficulty,
			numberOfWeeks: numberOfWeeks,
			firstSpeedInKg: firstSpeed ?? 0,
			targetDate,
		}
	},
}

/**
 * Calculates difference in kgs between initial and target weight on the event date
 * @param initialWeight {number} initial weight in kg
 * @param targetWeight {number} target weight in kg
 * @param eventDate {string | Date} event date in format which Date object could parse, for example 'YYYY-MM-DD' or Date object
 * @param targetDate {string | Date} target date in format which Date object could parse, for example 'YYYY-MM-DD' or Date object
 * @returns {number|null} how much weight user would lose in kgs
 */
export function calcEventWeightDifference({ initialWeight, targetWeight, eventDate, targetDate }) {
	if (!initialWeight || !targetWeight || !eventDate || !targetDate) {
		return null
	}
	const today = new Date()
	const ratio = Math.min(differenceInDays(new Date(eventDate), today) / differenceInDays(targetDate, today), 1)
	return (initialWeight - targetWeight) * ratio
}

/**
 * Computes expected weight loss in 1 month window in kilograms.
 * Picks `normal` predict targetDate if available, otherwise uses provided fallback target date.
 * Result is rounded to 0.1 kg and clamped to [1, 4] kg.
 * @param {Object} args
 * @param {number} args.initialWeightInKg
 * @param {number} args.targetWeightInKg
 * @param {Array<{difficulty:string,targetDate:Date}>} [args.predicts]
 * @param {string|Date} [args.fallbackTargetDate]
 * @returns {number|null} weight loss in kg (0.1 precision) or null if cannot be computed
 */
export function computeWeightLossIn1MonthKg({
	initialWeightInKg,
	targetWeightInKg,
	predicts = [],
	fallbackTargetDate,
}) {
	if (!initialWeightInKg || !targetWeightInKg) return null
	let targetDate = null
	const normal = predicts.find((p) => p.difficulty === 'normal') || predicts[0]
	if (normal?.targetDate) {
		targetDate = normal.targetDate
	} else if (fallbackTargetDate) {
		targetDate = new Date(fallbackTargetDate)
	} else {
		return null
	}
	const lossKg = calcEventWeightDifference({
		initialWeight: initialWeightInKg,
		targetWeight: targetWeightInKg,
		eventDate: addMonths(new Date(), 1),
		targetDate,
	})
	if (lossKg == null || Number.isNaN(lossKg)) return null
	const roundedLossKg = Math.round(lossKg * 10) / 10
	const MIN_KG = 1
	const MAX_KG = 4
	const kg = Math.min(Math.max(roundedLossKg, MIN_KG), MAX_KG)
	return kg
}
